/**
 * site-ui.js
 * ----------
 * - <site-swiper> : Swiper 슬라이더 래퍼
 * - <site-tabs>   : 탭 UI 컴포넌트
 * ----------
 */

/* =========================
   site-swiper 커스텀 엘리먼트
=========================== */
class SiteSwiper extends HTMLElement {
    constructor() {
      super();
      this.swiper = null;
      this._config = null;
      this._isDestroyed = false;
    }
  
    static get observedAttributes() { return ['data-config']; }
  
    // 기본 설정을 쉽게 오버라이드할 수 있는 메서드
    getDefaultConfig() {
      return {
        slidesPerView: 1,
        spaceBetween: 0,
        navigation: {
          nextEl: this.querySelector('.swiper-button-next'),
          prevEl: this.querySelector('.swiper-button-prev')
        },
        observer: true,
        observeParents: true,
        pagination: {
          el: this.querySelector('.swiper-pagination'),
          clickable: true
        },
        speed: 600
      };
    }
    
    // 기본 설정과 사용자 설정 병합
    mergeConfig(defaultConfig, customConfig) {
      const deepMerge = (target, source) => {
        if (!source) return target;
        
        for (const key in source) {
          const value = source[key];
          if (value && typeof value === 'object' && !Array.isArray(value)) {
            target[key] = target[key] || {};
            deepMerge(target[key], value);
          } else {
            target[key] = value;
          }
        }
        return target;
      };
      return deepMerge({ ...defaultConfig }, customConfig);
    }
  
    attributeChangedCallback(name, oldVal, newVal) {
      if (name === 'data-config' && oldVal !== newVal) {
        this._initSwiper();
      }
    }
  
    connectedCallback() {
      if (!this._isDestroyed) {
        this._render();
        this._initSwiper();
      }
    }
  
    disconnectedCallback() {
      this.destroy();
    }
  
    _render() {
      try {
        // 설정 파싱
        this._config = this._parseConfig();
  
        // DOM 요소 캐싱
        const elements = this._cacheElements();
        
        // Swiper 구조 생성 및 설정
        this._createSwiperStructure(elements);
  
      } catch(error) {
        console.error('Error in _render:', error);
      }
    }
  
    _parseConfig() {
      try {
        return JSON.parse(this.getAttribute('data-config') || '{}');
      } catch(e) {
        console.warn('Invalid data-config:', e);
        return {};
      }
    }
  
    _cacheElements() {
      return {
        slides: Array.from(this.querySelectorAll('.swiper-slide')),
        pagination: this.querySelector('.swiper-pagination'),
        prevBtn: this.querySelector('.swiper-button-prev'),
        nextBtn: this.querySelector('.swiper-button-next'),
        container: this.querySelector('.swiper') || this._createElement('div', 'swiper'),
        wrapper: this.querySelector('.swiper-wrapper') || this._createElement('div', 'swiper-wrapper')
      };
    }
  
    _createElement(tag, className) {
      const element = document.createElement(tag);
      element.className = className;
      return element;
    }
  
    _createSwiperStructure({ slides, pagination, prevBtn, nextBtn, container, wrapper }) {
      // 슬라이드 재배치
      slides.forEach(slide => wrapper.appendChild(slide));
      
      // 컨테이너에 wrapper 추가
      if (!container.contains(wrapper)) {
        container.appendChild(wrapper);
      }
  
      // 네비게이션 처리
      this._setupNavigation(container, prevBtn, nextBtn);
  
      // 페이지네이션 처리
      this._setupPagination(container, pagination);
  
      // 기존 내용 제거 후 새로운 구조 추가
      this.innerHTML = '';
      this.appendChild(container);
    }
  
    _setupNavigation(container, existingPrevBtn, existingNextBtn) {
      if (this._config.navigation !== false && this._config.navigation !== "false") {
        if (!existingPrevBtn && !existingNextBtn) {
          const buttonContainer = this._createElement('div', 'swiper-button-container');
          buttonContainer.innerHTML = `
            <div class="swiper-button-next"></div>
            <div class="swiper-button-prev"></div>
          `;
          container.appendChild(buttonContainer);
        } else {
          if (existingPrevBtn) container.appendChild(existingPrevBtn);
          if (existingNextBtn) container.appendChild(existingNextBtn);
        }
      }
    }
  
    _setupPagination(container, existingPagination) {
      if (this._config.pagination !== false && this._config.pagination !== "false") {
        if (!existingPagination) {
          const pagination = this._createElement('div', 'swiper-pagination');
          container.appendChild(pagination);
        } else {
          container.appendChild(existingPagination);
        }
      }
    }
  
    _initSwiper() {
      // 기존 Swiper 인스턴스 정리
      this.destroy();
      
      // 설정 병합
      const config = this._prepareSwiperConfig();
  
      // Swiper 인스턴스 생성
      try {
        this.swiper = new Swiper(this.querySelector('.swiper'), config);
        this._isDestroyed = false;
      } catch (error) {
        console.error('Error initializing Swiper:', error);
      }
    }
  
    _prepareSwiperConfig() {
      const defaultConfig = this.getDefaultConfig();
      const config = this.mergeConfig(defaultConfig, this._config);
  
      // 설정 최적화
      this._optimizeConfig(config);
  
      // 패럴럭스 설정
      if (config.parallax) {
        this._setupParallaxConfig(config);
      }
  
      return config;
    }
  
    _optimizeConfig(config) {
      // navigation이 false나 "false"인 경우 처리
      if (config.navigation === false || config.navigation === "false") {
        config.navigation = false;
      }
      
      // pagination이 false나 "false"인 경우 처리
      if (config.pagination === false || config.pagination === "false") {
        config.pagination = false;
      }
  
      // loop 모드 설정 처리
      if (config.loop) {
        const slides = this.querySelectorAll('.swiper-slide');
        if (slides.length < (config.slidesPerView || 1) * 2) {
          config.loop = false;  // 슬라이드 개수가 부족하면 loop 비활성화
        }
      }
    }
  
    _setupParallaxConfig(config) {
      config.watchSlidesProgress = true;
      config.parallax = true;
      config.speed = config.speed || 1000;
  
      config.on = {
        ...config.on,
        init: () => {
          requestAnimationFrame(() => this._initParallaxElements());
        },
        progress: (swiper, progress) => {
          this._handleParallax(swiper, progress);
        }
      };
    }
  
    _initParallaxElements() {
      if (!this.swiper) return;
  
      const parallaxElements = this._getParallaxElements();
      
      parallaxElements.forEach(el => {
        const speed = this.swiper.params.speed || 1000;
        const parallaxDuration = el.dataset.swiperParallaxDuration || speed;
        
        el.style.transitionDuration = `${parallaxDuration}ms`;
        
        // 초기 transform 설정
        const parallaxX = el.dataset.swiperParallaxX || el.dataset.swiperParallax || '0';
        const parallaxY = el.dataset.swiperParallaxY || '0';
        
        el.style.transform = `translate3d(${parallaxX}px, ${parallaxY}px, 0)`;
      });
    }
  
    _handleParallax(swiper, progress) {
      if (!swiper) return;
  
      const parallaxElements = this._getParallaxElements();
      
      parallaxElements.forEach(el => {
        const {
          swiperParallaxX: parallaxX = el.dataset.swiperParallax || '0',
          swiperParallaxY: parallaxY = '0',
          swiperParallaxOpacity: parallaxOpacity,
          swiperParallaxScale: parallaxScale
        } = el.dataset;
  
        const translateX = parseFloat(parallaxX) * (1 - progress);
        const translateY = parseFloat(parallaxY) * (1 - progress);
        
        let transform = `translate3d(${translateX}px, ${translateY}px, 0)`;
        
        if (parallaxScale) {
          const scale = 1 - ((1 - parseFloat(parallaxScale)) * (1 - Math.abs(progress)));
          transform += ` scale(${scale})`;
        }
        
        el.style.transform = transform;
        
        if (parallaxOpacity) {
          const opacity = parseFloat(parallaxOpacity) * Math.abs(progress) + (1 - parseFloat(parallaxOpacity));
          el.style.opacity = opacity;
        }
      });
    }
  
    _getParallaxElements() {
      return this.querySelectorAll(
        '[data-swiper-parallax], [data-swiper-parallax-x], [data-swiper-parallax-y], [data-swiper-parallax-opacity], [data-swiper-parallax-scale]'
      );
    }
  
    // Swiper 인스턴스 반환
    getSwiper() {
      return this.swiper;
    }
  
    // Swiper 인스턴스 제거
    destroy() {
      if (this.swiper && !this._isDestroyed) {
        this.swiper.destroy(true, true);
        this.swiper = null;
        this._isDestroyed = true;
      }
    }
  
    // 설정 가져오기
    getConfig() {
      return this._config;
    }
  }
  
  /* =========================
     site-tabs 커스텀 엘리먼트
  =========================== */
  class SiteTabs extends HTMLElement {
    connectedCallback() {
      // 기존 탭 구조에서 요소들과 클래스 추출
      const existingTabsEl = this.querySelector('.tab-names');
      const existingContentsEl = this.querySelector('.tab-contents');
      
      // 네비게이션 요소는 제외하고 실제 탭만 수집
      const tabNames = Array.from(existingTabsEl?.children || []).filter(child => 
        !child.classList.contains('swiper-navigation-shared')
      );
      const tabContents = Array.from(existingContentsEl?.children || []);
      
      // 기존 컨테이너의 클래스 보존
      const existingTabsClasses = existingTabsEl?.className || '';
      const existingContentsClasses = existingContentsEl?.className || '';
      
      // 기존 네비게이션 요소들 보존
      const navigationElements = Array.from(existingTabsEl?.children || []).filter(child => 
        child.classList.contains('swiper-navigation-shared')
      );
      
      // 기존 HTML에서 탭 관련이 아닌 다른 요소들 보존
      const otherElements = Array.from(this.children).filter(child => 
        !child.classList.contains('tab-names') && 
        !child.classList.contains('tab-contents')
      );
      
      // 기존 탭 구조 제거
      if (existingTabsEl) existingTabsEl.remove();
      if (existingContentsEl) existingContentsEl.remove();
      
      // 새로운 탭 구조 생성
      const tabsEl = document.createElement('div');
      tabsEl.className = existingTabsClasses || 'tab-names';
      tabsEl.setAttribute('role', 'tablist');
      
      const contentsEl = document.createElement('div');
      contentsEl.className = existingContentsClasses || 'tab-contents';
      
      // 새로운 탭 구조를 맨 앞에 추가
      this.insertBefore(contentsEl, this.firstChild);
      this.insertBefore(tabsEl, this.firstChild);
      
      // 기본 클래스가 없으면 추가
      if (!tabsEl.classList.contains('tab-names')) {
        tabsEl.classList.add('tab-names');
      }
      if (!contentsEl.classList.contains('tab-contents')) {
        contentsEl.classList.add('tab-contents');
      }
      
      // 데이터 속성으로 추가 클래스 적용
      const additionalTabsClasses = this.getAttribute('data-tabs-class');
      if (additionalTabsClasses && additionalTabsClasses.trim()) {
        additionalTabsClasses.split(' ').forEach(cls => {
          if (cls.trim()) tabsEl.classList.add(cls.trim());
        });
      }
      
      const additionalContentsClasses = this.getAttribute('data-contents-class');
      if (additionalContentsClasses && additionalContentsClasses.trim()) {
        additionalContentsClasses.split(' ').forEach(cls => {
          if (cls.trim()) contentsEl.classList.add(cls.trim());
        });
      }
  
      // 탭 버튼들 생성 및 이벤트 바인딩
      tabNames.forEach((tab, i) => {
        const tabEl = tab.cloneNode(true);
        // 기존 클래스 보존하고 필수 클래스만 추가
        if (!tabEl.classList.contains('tab-name')) {
          tabEl.classList.add('tab-name');
        }
        
        // 데이터 속성으로 추가 클래스 지정 가능
        const additionalTabClasses = this.getAttribute('data-tab-class');
        if (additionalTabClasses && additionalTabClasses.trim()) {
          additionalTabClasses.split(' ').forEach(cls => {
            if (cls.trim()) tabEl.classList.add(cls.trim());
          });
        }
        
        tabEl.setAttribute('role', 'tab');
        tabEl.setAttribute('tabindex', i === 0 ? '0' : '-1');
        tabEl.setAttribute('aria-selected', i === 0 ? 'true' : 'false');
        
        // 클릭 이벤트
        tabEl.onclick = () => this._activateTab(i);
        
        // 키보드 내비게이션
        tabEl.onkeydown = (e) => {
          const tabs = Array.from(this.querySelectorAll('.tab-name')).filter(tab => 
            !tab.classList.contains('swiper-navigation-shared')
          );
          let newIndex = i;
          
          switch(e.key) {
            case 'ArrowLeft':
              newIndex = i > 0 ? i - 1 : tabs.length - 1;
              break;
            case 'ArrowRight':
              newIndex = i < tabs.length - 1 ? i + 1 : 0;
              break;
            case 'Home':
              newIndex = 0;
              break;
            case 'End':
              newIndex = tabs.length - 1;
              break;
            default:
              return;
          }
          
          e.preventDefault();
          this._activateTab(newIndex);
          tabs[newIndex].focus();
        };
        
        tabsEl.appendChild(tabEl);
      });
      
      // 네비게이션 요소들 추가
      navigationElements.forEach(navEl => {
        tabsEl.appendChild(navEl);
      });
      
      // 탭 컨텐츠들 생성
      tabContents.forEach((content, i) => {
        const contentEl = content.cloneNode(true);
        // 기존 클래스 보존하고 필수 클래스만 추가
        if (!contentEl.classList.contains('tab-content')) {
          contentEl.classList.add('tab-content');
        }
        
        // 데이터 속성으로 추가 클래스 지정 가능
        const additionalContentClasses = this.getAttribute('data-content-class');
        if (additionalContentClasses && additionalContentClasses.trim()) {
          additionalContentClasses.split(' ').forEach(cls => {
            if (cls.trim()) contentEl.classList.add(cls.trim());
          });
        }
        
        contentEl.setAttribute('role', 'tabpanel');
        contentEl.setAttribute('tabindex', '0');
        contentsEl.appendChild(contentEl);
      });
      
      // 첫 번째 탭 활성화
      this._activateTab(0);
      
      // 스와이퍼 로드 완료 후 네비게이션 연결
      setTimeout(() => this._connectActiveTabNavigation(0), 100);
    }
  
    _activateTab(index) {
      // 네비게이션 요소를 제외한 실제 탭만 처리
      const actualTabs = Array.from(this.querySelectorAll('.tab-name')).filter(tab => 
        !tab.classList.contains('swiper-navigation-shared')
      );
      
      // 모든 탭과 컨텐츠의 활성 상태 토글
      actualTabs.forEach((tab, i) => {
        const isActive = i === index;
        tab.classList.toggle('active', isActive);
        tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
        tab.setAttribute('tabindex', isActive ? '0' : '-1');
      });
      this.querySelectorAll('.tab-content').forEach((content, i) => 
        content.classList.toggle('active', i === index)
      );
      
      // 현재 활성 탭의 스와이퍼 네비게이션 연동
      this._connectActiveTabNavigation(index);
    }
  
    _connectActiveTabNavigation(activeIndex) {
      // 활성 탭 컨텐츠 찾기
      const activeContent = this.querySelectorAll('.tab-content')[activeIndex];
      if (!activeContent) return;
      
      // 활성 탭의 스와이퍼 찾기
      const swiperElement = activeContent.querySelector('site-swiper');
      if (!swiperElement) return;
      
      const swiper = swiperElement.getSwiper();
      if (!swiper) return;
      
      // 공통 네비게이션 버튼 찾기
      const sharedNavNext = this.querySelector('.swiper-button-next-shared');
      const sharedNavPrev = this.querySelector('.swiper-button-prev-shared');
      
      if (sharedNavNext && sharedNavPrev) {
        // 기존 이벤트 제거
        sharedNavNext.onclick = null;
        sharedNavPrev.onclick = null;
        
        // 새로운 이벤트 연결 (이벤트 전파 차단 포함)
        sharedNavNext.onclick = (e) => {
          e.stopPropagation();
          swiper.slideNext();
        };
        sharedNavPrev.onclick = (e) => {
          e.stopPropagation();
          swiper.slidePrev();
        };
        
        // 버튼 상태 업데이트
        this._updateNavigationState(swiper, sharedNavNext, sharedNavPrev);
        
        // 슬라이드 변경 시 버튼 상태 업데이트
        swiper.off('slideChange.tabNav');
        swiper.on('slideChange.tabNav', () => {
          this._updateNavigationState(swiper, sharedNavNext, sharedNavPrev);
        });
      }
    }
  
    _updateNavigationState(swiper, nextBtn, prevBtn) {
      if (!swiper || !nextBtn || !prevBtn) return;
      
      // 버튼 상태 업데이트
      prevBtn.classList.toggle('swiper-button-disabled', swiper.isBeginning);
      nextBtn.classList.toggle('swiper-button-disabled', swiper.isEnd);
    }
  }
  
  
  // 커스텀 엘리먼트 등록
  customElements.define('site-swiper', SiteSwiper);
  customElements.define('site-tabs', SiteTabs);
  